/** Thin Web Speech API wrapper for Crisp-style dictate (Phase 9a). */

export interface SpeechRecognitionAlternativeLike {
  readonly transcript: string;
}

export interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternativeLike;
}

export interface SpeechRecognitionEventLike {
  readonly resultIndex: number;
  readonly results: {
    readonly length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
}

export interface SpeechRecognitionErrorEventLike {
  readonly error: string;
  readonly message?: string;
}

export interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

export type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

export type SpeechRecognitionScope = Record<string, unknown>;

export function resolveSpeechRecognitionCtor(
  scope: SpeechRecognitionScope = globalThis as unknown as SpeechRecognitionScope,
): SpeechRecognitionConstructor | null {
  const ctor = scope['SpeechRecognition'] || scope['webkitSpeechRecognition'];
  return typeof ctor === 'function'
    ? (ctor as SpeechRecognitionConstructor)
    : null;
}

export function isSpeechInputSupported(
  scope: SpeechRecognitionScope = globalThis as unknown as SpeechRecognitionScope,
): boolean {
  return resolveSpeechRecognitionCtor(scope) !== null;
}

/** Soft user-facing copy for SpeechRecognition error codes. */
export function speechErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone permission denied.';
    case 'no-speech':
      return 'No speech detected. Try again.';
    case 'audio-capture':
      return 'No microphone found.';
    case 'network':
      return 'Speech recognition network error.';
    case 'aborted':
      return '';
    default:
      return 'Speech input failed. Try again.';
  }
}

/**
 * Prefer a leading space when inserting speech after non-whitespace.
 * Caller still uses insertTextAtCursor for caret placement.
 */
export function prepareSpeechTranscriptForInsert(
  value: string,
  transcript: string,
  caretStart: number,
): string {
  const text = transcript.trim();
  if (!text) return '';
  if (caretStart <= 0) return text;
  const before = value.slice(0, Math.min(caretStart, value.length));
  if (!before || /\s$/.test(before)) return text;
  return ` ${text}`;
}

export interface ComposerSpeechListener {
  onListeningChange: (listening: boolean) => void;
  onInterim: (transcript: string) => void;
  onFinal: (transcript: string) => void;
  onError: (message: string) => void;
}

/**
 * Owns one SpeechRecognition instance at a time.
 * Final transcripts are emitted as they finalize; interim stays out of the draft.
 */
export class ComposerSpeechController {
  private recognition: SpeechRecognitionLike | null = null;
  private listening = false;

  constructor(
    private readonly listener: ComposerSpeechListener,
    private readonly scope: SpeechRecognitionScope = globalThis as unknown as SpeechRecognitionScope,
  ) {}

  get supported(): boolean {
    return isSpeechInputSupported(this.scope);
  }

  get isListening(): boolean {
    return this.listening;
  }

  toggle(): void {
    if (this.listening) {
      this.stop();
    } else {
      this.start();
    }
  }

  start(): void {
    if (this.listening) return;
    const Ctor = resolveSpeechRecognitionCtor(this.scope);
    if (!Ctor) {
      this.listener.onError('Speech input not supported in this browser.');
      return;
    }

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang =
      typeof navigator !== 'undefined' && navigator.language
        ? navigator.language
        : 'en-US';

    recognition.onresult = (event) => {
      let interim = '';
      let finalChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const piece = result?.[0]?.transcript ?? '';
        if (result?.isFinal) {
          finalChunk += piece;
        } else {
          interim += piece;
        }
      }
      if (finalChunk.trim()) {
        this.listener.onFinal(finalChunk);
      }
      this.listener.onInterim(interim);
    };

    recognition.onerror = (event) => {
      const message = speechErrorMessage(event?.error || '');
      if (message) {
        this.listener.onError(message);
      }
    };

    recognition.onend = () => {
      this.recognition = null;
      if (!this.listening) return;
      this.listening = false;
      this.listener.onListeningChange(false);
      this.listener.onInterim('');
    };

    try {
      recognition.start();
    } catch {
      this.listener.onError('Speech input failed. Try again.');
      return;
    }

    this.recognition = recognition;
    this.listening = true;
    this.listener.onListeningChange(true);
  }

  stop(): void {
    const recognition = this.recognition;
    if (!recognition) {
      if (this.listening) {
        this.listening = false;
        this.listener.onListeningChange(false);
        this.listener.onInterim('');
      }
      return;
    }
    try {
      recognition.stop();
    } catch {
      try {
        recognition.abort();
      } catch {
        /* ignore */
      }
      this.recognition = null;
      this.listening = false;
      this.listener.onListeningChange(false);
      this.listener.onInterim('');
    }
  }

  /** Hard stop for destroy / navigation — no error toast. */
  dispose(): void {
    const recognition = this.recognition;
    this.recognition = null;
    this.listening = false;
    if (!recognition) return;
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    try {
      recognition.abort();
    } catch {
      /* ignore */
    }
  }
}
